import { useEffect } from 'react';
import { createSelector } from 'reselect';

import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_PROPERTY_CONFIG,
  INITIAL_PROPERTIES,
} from '@/tools/conditionWithProperties/conditionWithProperties.constants';
import { getUpdatedConditionProperty } from '@/tools/conditionWithProperties/conditionWithProperties.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ConditionProperty } from '@/tools/conditionWithProperties/conditionWithProperties.types';
import type {
  BaseToolPersistedState,
  ToolConfigParams,
  ToolConfigValue,
  ToolRehydratePayload,
} from '@/tools/tool.types';

export interface ConditionWithPropertiesState extends BaseToolPersistedState {
  /** The string-signal and output-property-name pairs used to build the condition. */
  properties: ConditionProperty[];
}

export interface ConditionWithPropertiesActions {
  /** Sets the user-visible name of the output condition. */
  setSearchName: (name: string) => void;
  /** Collapses or expands the Advanced Parameters section. */
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  /** Adds an empty property configuration. */
  addProperty: () => void;
  /** Removes the property configuration at the given index. */
  removeProperty: (index: number) => void;
  /** Merges changes into the property configuration at the given index. */
  updateProperty: (payload: { value: Partial<ConditionProperty>; index: number }) => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: ResettableStoreState<ConditionWithPropertiesState>;
}

export type ConditionWithPropertiesDehydratedState = ConditionWithPropertiesState;

const initialState: ConditionWithPropertiesState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  properties: INITIAL_PROPERTIES.map((property) => ({ ...property })),
});

export const dehydrateConditionWithPropertiesState = (
  state: ConditionWithPropertiesState,
): ConditionWithPropertiesDehydratedState => state;

const conditionWithPropertiesActionsFactory: ActionsFactory<
  ConditionWithPropertiesState,
  ConditionWithPropertiesActions
> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  addProperty: () =>
    set((draft) => {
      draft.properties.push({ ...DEFAULT_PROPERTY_CONFIG });
    }),

  removeProperty: (index) =>
    set((draft) => {
      if (index > -1) {
        draft.properties.splice(index, 1);
      }
    }),

  updateProperty: ({ value, index }) =>
    set((draft) => {
      if (index < 0) {
        return;
      }

      draft.properties.splice(index, 1, getUpdatedConditionProperty(draft.properties[index], value));
    }),

  rehydrateForEdit: (payload) => {
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, rehydrationState.config, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'ConditionWithProperties';

const [
  ConditionWithPropertiesStoreProvider,
  useConditionWithPropertiesStoreInstance,
  useConditionWithPropertiesStoreSelector,
] = createStore(STORE_NAME, initialState, conditionWithPropertiesActionsFactory);

export { ConditionWithPropertiesStoreProvider };

const conditionWithPropertiesConfigParamsSelector = createSelector(
  [(state: ConditionWithPropertiesState) => state],
  (state): ToolConfigParams =>
    getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.CONDITION_WITH_PROPERTIES,
      parameterDefinitionKeys: [],
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    }),
);

/** The unique ID of the output condition. */
export const useConditionWithPropertiesId = () => useConditionWithPropertiesStoreSelector((state) => state.id);
/** The user-visible name of the output condition. */
export const useConditionWithPropertiesName = () => useConditionWithPropertiesStoreSelector((state) => state.name);
/** The original parameter items hydrated from a saved tool configuration. */
export const useConditionWithPropertiesOriginalParameters = () =>
  useConditionWithPropertiesStoreSelector((state) => state.originalParameters);
/** The maximum duration configured for the output condition. */
export const useConditionWithPropertiesMaximumDuration = () =>
  useConditionWithPropertiesStoreSelector((state) => state.maximumDuration);
/** Whether the Advanced Parameters section is collapsed. */
export const useConditionWithPropertiesAdvancedParametersCollapsed = () =>
  useConditionWithPropertiesStoreSelector((state) => state.advancedParametersCollapsed);
/** The version of the saved tool configuration. */
export const useConditionWithPropertiesConfigVersion = () =>
  useConditionWithPropertiesStoreSelector((state) => state.configVersion);
/** The string-signal and output-property-name pairs used to build the condition. */
export const useConditionWithPropertiesProperties = () =>
  useConditionWithPropertiesStoreSelector((state) => state.properties);
/** The UI configuration persisted with the calculated condition. */
export const useConditionWithPropertiesConfigParams = () =>
  useConditionWithPropertiesStoreSelector(conditionWithPropertiesConfigParamsSelector);

/** Returns the Condition With Properties store actions for updating state. */
export const useConditionWithPropertiesActions = () => useConditionWithPropertiesStoreInstance().actions;

export function createTestConditionWithPropertiesStore(overrides?: Partial<ConditionWithPropertiesState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestConditionWithPropertiesStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, conditionWithPropertiesActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateConditionWithPropertiesStore() {
  return createStoreInstance<ConditionWithPropertiesState, ConditionWithPropertiesActions>(
    initialState,
    conditionWithPropertiesActionsFactory,
    {
      storeName: STORE_NAME,
    },
  );
}

export const usePersistConditionWithPropertiesStore = () => {
  const register = useRegisterPersistedStore();
  const store = useConditionWithPropertiesStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqConditionWithPropertiesStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateConditionWithPropertiesState,
    });
  }, [register, store]);
};
